import React from 'react';

/** Shared shape for affiliation fields across People / Experience / Content. */
export type AffiliationFields = {
  client_id?: number | null;
  client_name?: string | null;
  group_name?: string | null;
};

/**
 * Max chars that fit the tighter affiliation surface (peer tile top strip /
 * profile avatar bottom strip) without clipping. Longer names become an acronym.
 */
export const AFFILIATION_LABEL_MAX_LENGTH = 20;

/**
 * Label for affiliation banners.
 * Displayed when the tile's peer/content is affiliated with the signed-in member's client.
 */
export function getAffiliationClientName(
  item?: AffiliationFields | null,
  viewerClientId?: number | null,
): string | null {
  if (!item || viewerClientId == null || item.client_id == null) {
    return null;
  }
  if (Number(item.client_id) !== Number(viewerClientId)) {
    return null;
  }
  const label = (item.client_name || item.group_name || '').trim();
  return label || null;
}

/**
 * First letter of each whitespace-separated word, uppercased.
 * "LA Academia Partnership Charter School" → "LAPCS"
 */
export function toAffiliationAcronym(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Display label for affiliation banners: full name when it fits, otherwise acronym.
 * Single oversized words (no spaces) fall back to a hard slice so we never overflow.
 */
export function formatAffiliationBannerLabel(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length <= AFFILIATION_LABEL_MAX_LENGTH) {
    return trimmed;
  }

  const acronym = toAffiliationAcronym(trimmed);
  if (acronym.length > 1) {
    return acronym;
  }

  return trimmed.slice(0, AFFILIATION_LABEL_MAX_LENGTH);
}

interface AffiliationBannerProps {
  clientName: string;
  /**
   * `top-strip` — full-width bar across the top of a tile (default).
   * `avatar-bottom` — horizontal strip near the bottom of a circular avatar;
   *   parent must be `overflow-hidden rounded-full` so the circle clips the sides.
   */
  variant?: 'top-strip' | 'avatar-bottom';
  /** Use absolute positioning for `top-strip`. Default true. Ignored for avatar-bottom. */
  absolute?: boolean;
  className?: string;
}

/**
 * Affiliation label for peer / experience surfaces.
 * Uses the signed-in member's brand primary; label is that client name
 * (or an acronym when the full name would overflow the banner).
 */
export const AffiliationBanner: React.FC<AffiliationBannerProps> = ({
  clientName,
  variant = 'top-strip',
  absolute = true,
  className = '',
}) => {
  const displayLabel = formatAffiliationBannerLabel(clientName);

  if (variant === 'avatar-bottom') {
    // Full-bleed strip near the bottom; parent must be overflow-hidden
    // rounded-full so the circle clips the strip's left/right edges.
    return (
      <div
        className={`absolute inset-x-0 bottom-[14%] z-10 flex h-6 items-center justify-center bg-[rgb(var(--color-primary))] px-4 md:bottom-[16%] md:h-7 ${className}`}
        aria-label={`Affiliated with ${clientName}`}
        title={clientName}
      >
        <span className="font-albert-sans text-[10px] font-semibold uppercase tracking-[0.06em] text-white md:text-xs">
          {displayLabel}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${
        absolute ? 'absolute inset-x-0 top-0 z-10' : 'relative w-full'
      } flex h-6 items-center justify-center bg-[rgb(var(--color-primary))] px-2 ${className}`}
      aria-label={`Affiliated with ${clientName}`}
      title={clientName}
    >
      <span className="font-albert-sans text-[10px] font-semibold uppercase tracking-[0.06em] text-white">
        {displayLabel}
      </span>
    </div>
  );
};
